import { Box, Button, Stack, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { ReactComponent as Affinity } from "../../../assets/logo/affinity.svg";
import { ReactComponent as Airtable } from "../../../assets/logo/airtable-full.svg";
import { ReactComponent as Attio } from "../../../assets/logo/attio.svg";
import { ReactComponent as Hubspot } from "../../../assets/logo/hubspot-full.svg";
import { ReactComponent as Salesforce } from "../../../assets/logo/salesforce.svg";
import { ReactComponent as Zapier } from "../../../assets/logo/zapier.svg";
import ManageFieldsLoaderModal from "../../modals/home-manage-fields/ManageFieldsLoaderModal";
import AddCompanyCard from "./AddCompanyCard";
import CompanyBulkCSVCard from "./CompanyBulkCSVCard";
import ForwardDealsCard from "./ForwardDealsCard";

const logoStyle = {
    width: 80,
    height: 80,
};

const useStyles = makeStyles((theme) => ({
    superImage: {
        width: 150,
        height: 150,
    },
    image: {
        width: 80,
        height: 'auto',
    },
    superText: {
        fontFamily: 'Inter',
        fontSize: '1.3rem',
        fontWeight: 500,
        color: '#666666',
        textAlign: 'center',
    },
    linkedIcon: {
        width: 80,
        height: 80,
    },
    mainText: {
        fontFamily: 'Inter',
        fontSize: '1.2rem',
        fontWeight: 500,
        color: theme.colors.neutral['600'],
    },
    mainLabel: {
        fontFamily: 'Inter',
        fontSize: '1.3rem',
        fontWeight: 'bold',
        color: theme.colors.neutral['800'],
    },
    secondText: {
        fontFamily: 'Lato',
        fontSize: '1.2rem',
        fontWeight: 400,
        color: theme.colors.neutral['600'],
        textAlign: 'center',
    },
    thirdText: {
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: 400,
        color: theme.colors.neutral['600'],
    },
    subText: {
        fontFamily: 'Inter',
        fontSize: '1.1rem',
        fontWeight: 400,
        color: theme.colors.neutral['600'],
    },
    link: {
        fontFamily: 'Inter',
        fontSize: '1.1rem',
        fontWeight: 'bold',
        color: theme.colors.primary['500'],
        cursor: 'pointer',
    },
    contact: {
        fontFamily: 'Inter',
        fontSize: '1.1rem',
        fontWeight: '500',
        color: theme.palette.primary.main,
        textDecoration: 'underline',
        cursor: 'pointer',
    },
    manageButton: {
        paddingLeft: 24,
        paddingRight: 24,
        borderRadius: 32,
        fontSize: '1.2rem',
        fontWeight: 'bold',
        textTransform: 'none',
        transition: 'ease-in-out 300ms',
        "& > .MuiButton-startIcon": {
            width: 24,
            height: 24,
        },
    },
}));

const NoDashboardsFoundView: React.FC<{
    noDataYet?: boolean,
    noDataYetModal?: boolean,
    noCompaniesFound?: boolean,
    noCompaniesYet?: boolean,
}> = ({ noDataYet, noDataYetModal, noCompaniesYet, noCompaniesFound, }) => {
    const classes = useStyles();
    const [manageFieldsModalOpen, setManageFieldsModalOpen] = useState<boolean>(false);

    return (<>
        {(noDataYet) ? (<>
            <Stack alignItems="center" justifyContent="flex-start" width="100%" height="100%" pt={9}>
                <img src="../../images/n-logo-white-bg.png" alt="n-logo" className={classes.superImage} />
                <Typography className={classes.superText} mt={3}>
                    <span>{'Notissia automates CRM data entry, pipeline '}</span><br />
                    <span>{'management, and company tracking.'}</span>
                </Typography>
                <Button variant="contained"
                    className={classes.manageButton} sx={{ my: 6 }}
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setManageFieldsModalOpen(true);
                    }} disableRipple>{'Build your ideal workflow'} </Button>
                <Typography className={classes.superText} fontWeight="unset !important">
                    <span>{'And connect it to:'}</span>
                </Typography>
                <Stack direction="row" spacing={6} alignItems="center" justifyContent="center" width="100%" mt={4}>
                    <Salesforce style={logoStyle} />
                    <Hubspot style={logoStyle} />
                    <Affinity style={logoStyle} />
                    <Attio style={{ width: 80, height: 'auto', borderRadius: 16 }} />
                    <Airtable style={{ width: 120, height: 'auto' }} />
                    <Zapier style={logoStyle} />
                    <Typography className={classes.subText}>{'and more ...'}</Typography>
                </Stack>
                <Typography className={classes.subText} mt={5}>
                    <span>{'To set up your ideal workflow, contact us at '}</span>
                    <CopyToClipboard text={`support@notissia.com`}
                        options={{ format: "text/plain" }}
                        onCopy={() => {
                            enqueueSnackbar(`Successfully copied to clipboard.`,
                                { anchorOrigin: { vertical: "bottom", horizontal: "right" }, });
                        }}>
                        <Typography component="span" className={classes.contact}
                            onClick={() => window.open(`mailto: support@notissia.com`, '_self', 'noopener, noreferrer')}
                            display="inline">
                            {`support@notissia.com`}
                        </Typography>
                    </CopyToClipboard>
                    <span>.</span>
                </Typography>
                <Box pb={9}>&nbsp;</Box>
            </Stack>
        </>) : (noDataYetModal) ? (<>
            <Stack alignItems="center" justifyContent="center" width="100%" height="100%" mt={10}>
                <img src="../../images/missing-dashboard.png" alt="missing-dashboard" className={classes.image} />
                <Typography className={classes.thirdText} mt={3}>{`You don't yet have companies in Notissia.`}</Typography>
                <Stack spacing={1} alignItems="center" justifyContent="center" width="100%" mt={5}>
                    <Typography className={classes.mainText} color="#666666 !important">{`How to add companies to Notissia`}</Typography>
                    <Typography className={classes.thirdText}>{`(maximum 200 companies per month):`}</Typography>
                </Stack>
                <Stack direction="row" spacing={8} alignItems="center" justifyContent="center" width="100%" height="100%">
                    <ForwardDealsCard />
                    <AddCompanyCard  />
                    <CompanyBulkCSVCard />
                </Stack>
            </Stack>
        </>) : (noCompaniesYet) ? (<>
            <Stack alignItems="center" justifyContent="center" width="100%" height="100%">
                <img src="../../images/missing-dashboard.png" alt="missing-dashboard" className={classes.image} />
                <Typography className={classes.mainText} mt={4}>{'No companies yet!'}</Typography>
            </Stack>
        </>) : (noCompaniesFound) ? (<>
            <Stack alignItems="center" justifyContent="center" width="100%" height="100%">
                <img src="../../images/missing-dashboard.png" alt="missing-dashboard" className={classes.image} />
                <Typography className={classes.secondText} mt={4}>{'No companies found!'}</Typography>
            </Stack>
        </>) : (<>
            <Stack alignItems="center" spacing={1} justifyContent="flex-start" width="100%" height="100%" mt={5}>
                <img src="../../images/missing-dashboard.png" alt="missing-dashboard" className={classes.image} />
            </Stack>
        </>)}
        {manageFieldsModalOpen && (
            <ManageFieldsLoaderModal
                open={manageFieldsModalOpen}
                onClose={() => setManageFieldsModalOpen(false)}
            />
        )}
    </>);
}

export default NoDashboardsFoundView;
